/**
 * CollapsibleSection
 * ---------------------------------------------------------------------------
 * Lightweight expand/collapse wrapper used on the Mechanic Productivity tab.
 * Renders a header bar with a title + chevron toggle; the children only
 * mount when expanded so nothing inside fetches its own data until the SM
 * actually opens the section.
 *
 * State is persisted to localStorage so the SM's open/closed preferences
 * survive page reloads — without that, a page refresh would slam every
 * section open and re-trigger every backend query.
 *
 * Usage:
 *   <CollapsibleSection id="mech-hours-entry" title="Mechanic Daily Hour Entry"
 *                       defaultOpen icon={<Users className="h-4 w-4" />}>
 *     <MyExpensiveChildren />
 *   </CollapsibleSection>
 */
import { useState, useCallback } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const LS_PREFIX = 'fin_tab_section_';

function readPref(id, fallback) {
  try {
    const v = window.localStorage.getItem(LS_PREFIX + id);
    if (v === '1') return true;
    if (v === '0') return false;
  } catch (_e) { /* localStorage disabled — fall through */ }
  return fallback;
}

function writePref(id, open) {
  try { window.localStorage.setItem(LS_PREFIX + id, open ? '1' : '0'); }
  catch (_e) { /* ignore */ }
}

export default function CollapsibleSection({
  id,
  title,
  subtitle,
  icon = null,
  defaultOpen = true,
  rightAccessory = null,
  children,
}) {
  const [open, setOpen] = useState(() => readPref(id, defaultOpen));

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      writePref(id, next);
      return next;
    });
  }, [id]);

  return (
    <div
      className="border border-slate-200 rounded-lg bg-white overflow-hidden"
      data-testid={`collapsible-${id}`}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer"
        data-testid={`collapsible-${id}-toggle`}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1 text-left">
          {icon && <span className="text-slate-600 flex-shrink-0">{icon}</span>}
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-slate-900 truncate">{title}</h3>
            {subtitle && (
              <p className="text-xs text-slate-500 truncate">{subtitle}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {rightAccessory}
          {open
            ? <ChevronUp className="h-4 w-4 text-slate-500" />
            : <ChevronDown className="h-4 w-4 text-slate-500" />}
        </div>
      </div>
      {open && (
        <div className="border-t border-slate-200" data-testid={`collapsible-${id}-body`}>
          {children}
        </div>
      )}
    </div>
  );
}
